/**
 * CommandPanel.ts
 * Bottom action bar — replaces SelectionPanel.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [swatch]  Name / class / HP bar  │  [X][G][C][F]   │
 *   └──────────────────────────────────────────────────────┘
 *
 * Left section: unit portrait, name, class, HP bar (single) or
 *               class breakdown with coloured dots (multi-select).
 * Right section: action buttons — which appear depends on selection;
 *                enabled/disabled state reflects live game conditions.
 *
 * Action hotkeys (X/G/C/F) are routed here via handleKey() from Game.ts.
 * The panel is purely UI — all game logic lives in the lambdas passed by
 * Game.ts at construction time.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Unit }     from '../entities/Unit';
import type { BandClass } from '../types';
import { CLASS_COLOUR }  from '../rendering/UnitRenderer';

// ── Layout ────────────────────────────────────────────────────────────────
/** Desktop single-row height. Keep exported for any legacy callers. */
export const COMMAND_PANEL_H = 100;
/** Tall height used when buttons wrap to two rows (mobile / narrow screens). */
const COMMAND_PANEL_H_TALL   = 144;

const INFO_W_FULL    = 340;   // Info section width on desktop
const BTN_W          = 84;
const BTN_H          = 62;
const BTN_GAP        = 10;
const COMPACT_BTN_H  = 46;   // Button height in two-row mode
const COMPACT_BTN_GAP = 6;   // Gap between buttons in two-row mode

// ── Colours ───────────────────────────────────────────────────────────────
const BG_COL    = 0x080b10;
const PANEL_BDR = 0x1a3a30;
const ACCENT    = 0x00ffcc;

// ── Text styles ────────────────────────────────────────────────────────────
const S_TITLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 13, fontWeight: 'bold',
  fill: '#c9fff8',
});
const S_DIM = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: 10,
  fill: '#3d6660',
});

// ── Button descriptor ─────────────────────────────────────────────────────
export interface CommandButton {
  /** Main display label */
  label:    string;
  /** Single keyboard character hotkey */
  hotkey:   string;
  /** Optional small sub-label below the main label. Can be a function for dynamic content. */
  sub?:     string | ((units: Unit[]) => string | undefined);
  /** Fill colour when enabled */
  colour:   number;
  /** Return false to hide this button entirely for the current selection */
  visible:  (units: Unit[]) => boolean;
  /** Return false to show the button greyed-out (present but unclickable) */
  enabled:  (units: Unit[]) => boolean;
  /** Execute the action */
  execute:  (units: Unit[]) => void;
}

export class CommandPanel {
  private readonly panel   = new Container();
  private readonly bgGfx   = new Graphics();
  private currentUnits: Unit[] = [];
  private screenW = 1280;
  private screenH = 720;

  constructor(ui: Container, private readonly buttons: CommandButton[]) {
    this.panel.addChild(this.bgGfx);
    ui.addChild(this.panel);
    this.panel.visible = false;
  }

  /**
   * Current panel height — 100px (single row) or 144px (two-row wrap).
   * Game.ts reads this for pointer hit-testing instead of the exported constant.
   */
  get panelHeight(): number {
    return this.needsTwoRows() ? COMMAND_PANEL_H_TALL : COMMAND_PANEL_H;
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    this.panel.position.set(0, h - this.panelHeight);
    if (this.panel.visible) this.rebuild();
  }

  show(units: Unit[]): void {
    this.currentUnits = units.filter(u => u.alive);
    if (this.currentUnits.length === 0) { this.hide(); return; }
    this.rebuild();
    this.panel.visible = true;
  }

  hide(): void {
    this.panel.visible = false;
    this.currentUnits  = [];
  }

  /**
   * Route a keyboard hotkey to the matching button.
   * Returns true if the key was consumed, false otherwise.
   * Called by Game.ts in its keydown handler.
   */
  handleKey(key: string): boolean {
    if (!this.panel.visible) return false;
    const k = key.toLowerCase();
    for (const btn of this.buttons) {
      if (
        btn.hotkey.toLowerCase() === k
        && btn.visible(this.currentUnits)
        && btn.enabled(this.currentUnits)
      ) {
        btn.execute(this.currentUnits);
        this.rebuild(); // Refresh button states after action
        return true;
      }
    }
    return false;
  }

  // ── Build ──────────────────────────────────────────────────────────────

  /** Force-refresh the panel with the current unit selection. */
  rebuild(): void {
    // Remove all children except the permanent background (index 0)
    while (this.panel.children.length > 1) this.panel.removeChildAt(1);

    // Reposition in case height changed (e.g. selection went from 3 buttons to 10).
    this.panel.position.set(0, this.screenH - this.panelHeight);

    // ── Responsive info section width ──────────────────────────────────────
    // On narrow screens (< 520 px) hide the info section entirely so the
    // full width is available for action buttons. Between 520–800 px use a
    // proportionally reduced info area.
    const infoW = this.screenW < 520
      ? 0
      : Math.min(INFO_W_FULL, Math.floor(this.screenW * 0.42));
    const btnStartX = infoW > 0 ? infoW + 20 : 16;

    this.drawBackground();

    const units = this.currentUnits;
    if (infoW > 0) {
      if (units.length === 1) this.drawSingle(units[0], infoW);
      else                    this.drawMulti(units, infoW);
      this.drawDivider(btnStartX);
    }
    this.drawButtons(units, btnStartX);
  }

  // ── Layout helpers ────────────────────────────────────────────────────────

  private infoStartX(): number {
    return this.screenW < 520
      ? 16
      : Math.min(INFO_W_FULL, Math.floor(this.screenW * 0.42)) + 20;
  }

  private needsTwoRows(): boolean {
    const visible  = this.buttons.filter(b => b.visible(this.currentUnits));
    const rightW   = this.screenW - this.infoStartX() - 16;
    return visible.length * BTN_W + Math.max(0, visible.length - 1) * BTN_GAP > rightW;
  }

  private drawBackground(): void {
    const h = this.panelHeight;
    this.bgGfx.clear();
    this.bgGfx.beginFill(BG_COL, 0.92);
    this.bgGfx.drawRect(0, 0, this.screenW, h);
    this.bgGfx.endFill();
    this.bgGfx.lineStyle(1, PANEL_BDR, 0.65);
    this.bgGfx.moveTo(0, 0);
    this.bgGfx.lineTo(this.screenW, 0);
    this.bgGfx.lineStyle(0);
  }

  // ── Info section ────────────────────────────────────────────────────────

  private drawSingle(unit: Unit, infoW: number): void {
    const pad    = 16;
    const colour = unit.bandClass ? CLASS_COLOUR[unit.bandClass] : 0xff3333;
    const midY   = this.panelHeight / 2;

    // Colour swatch
    const sw = new Graphics();
    sw.beginFill(colour, 0.9);
    sw.drawRoundedRect(0, 0, 46, 46, 4);
    sw.endFill();
    if (unit.isHero) {
      sw.lineStyle(2, 0xffd700, 0.8);
      sw.drawRoundedRect(-1, -1, 48, 48, 5);
      sw.lineStyle(0);
    }
    sw.position.set(pad, midY - 23);
    this.panel.addChild(sw);

    const tx = pad + 60;

    // Name
    const name = new Text(unit.displayName, S_TITLE);
    name.position.set(tx, midY - 28);
    this.panel.addChild(name);

    // Class / status line
    const parts: string[] = [];
    if (unit.isHero)                  parts.push('Hero');
    if (unit.bandClass)               parts.push(unit.bandClass);
    if (unit.isMining)                parts.push('⛏ mining');
    if (unit.faction !== undefined && unit.faction as unknown as string === 'neutral') parts.push('neutral');
    const clsText = new Text(parts.join('  ·  '), S_DIM);
    clsText.position.set(tx, midY - 9);
    this.panel.addChild(clsText);

    // HP bar
    const barW  = Math.min(150, infoW - tx - 20);
    const barY  = midY + 10;
    const hpPct = Math.max(0, unit.hp / unit.maxHp);
    const hpCol = hpPct > 0.5 ? 0x44cc66 : hpPct > 0.25 ? 0xffaa00 : 0xff3333;

    const hpBar = new Graphics();
    hpBar.beginFill(0x1a1a1a, 0.9);
    hpBar.drawRoundedRect(tx, barY, barW, 5, 1);
    hpBar.endFill();
    hpBar.beginFill(hpCol);
    hpBar.drawRoundedRect(tx, barY, barW * hpPct, 5, 1);
    hpBar.endFill();
    this.panel.addChild(hpBar);

    const hpLbl = new Text(`${unit.hp}/${unit.maxHp}`, S_DIM);
    hpLbl.position.set(tx, barY + 8);
    this.panel.addChild(hpLbl);
  }

  private drawMulti(units: Unit[], infoW: number): void {
    const pad = 16;

    const title = new Text(`${units.length} units selected`, S_TITLE);
    title.position.set(pad, 14);
    this.panel.addChild(title);

    const counts = new Map<BandClass | 'enemy', number>();
    for (const u of units) {
      const key: BandClass | 'enemy' = u.bandClass ?? 'enemy';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    let x = pad, y = 48;
    for (const [cls, count] of counts) {
      const colour = cls === 'enemy' ? 0xff3333 : CLASS_COLOUR[cls as BandClass];

      const dot = new Graphics();
      dot.beginFill(colour).drawCircle(0, 0, 4).endFill();
      dot.position.set(x + 4, y + 7);
      this.panel.addChild(dot);

      const lbl = new Text(`${count} ${cls}`, S_DIM);
      lbl.position.set(x + 13, y);
      this.panel.addChild(lbl);

      x += lbl.width + 20;
      if (x > infoW - 30) break;
    }

    // Mining badge
    const mining = units.filter(u => u.isMining).length;
    if (mining > 0) {
      const mt = new Text(`${mining} mining ⛏`, S_DIM);
      mt.position.set(pad, y + 20);
      this.panel.addChild(mt);
    }
  }

  private drawDivider(btnStartX: number): void {
    const dx = btnStartX - 10;
    const div = new Graphics();
    div.lineStyle(1, PANEL_BDR, 0.5);
    div.moveTo(dx, 10);
    div.lineTo(dx, this.panelHeight - 10);
    div.lineStyle(0);
    this.panel.addChild(div);
  }

  // ── Button section ───────────────────────────────────────────────────────

  private drawButtons(units: Unit[], btnStartX: number): void {
    const visible = this.buttons.filter(b => b.visible(units));
    if (visible.length === 0) return;

    const rightW  = this.screenW - btnStartX - 16;
    const oneRowW = visible.length * BTN_W + Math.max(0, visible.length - 1) * BTN_GAP;

    if (oneRowW <= rightW) {
      // ── Single row (desktop / wide screen) ───────────────────────────────
      const startX = btnStartX + Math.max(0, (rightW - oneRowW) / 2);
      const y      = (COMMAND_PANEL_H - BTN_H) / 2;
      for (let i = 0; i < visible.length; i++) {
        this.drawButton(visible[i], startX + i * (BTN_W + BTN_GAP), y, BTN_W, BTN_H, units);
      }
    } else {
      // ── Two-row compact layout (mobile / narrow screen) ──────────────────
      const perRow  = Math.ceil(visible.length / 2);
      const btnW    = Math.min(BTN_W, Math.floor((rightW - (perRow - 1) * COMPACT_BTN_GAP) / perRow));
      const rowInnerH = COMPACT_BTN_H * 2 + COMPACT_BTN_GAP;
      const row1Y   = Math.floor((COMMAND_PANEL_H_TALL - rowInnerH) / 2);
      const row2Y   = row1Y + COMPACT_BTN_H + COMPACT_BTN_GAP;
      const totalRowW = perRow * btnW + (perRow - 1) * COMPACT_BTN_GAP;
      const startX  = btnStartX + Math.max(0, (rightW - totalRowW) / 2);

      for (let i = 0; i < visible.length; i++) {
        const row = i < perRow ? 0 : 1;
        const col = i < perRow ? i : i - perRow;
        const x   = startX + col * (btnW + COMPACT_BTN_GAP);
        const y   = row === 0 ? row1Y : row2Y;
        this.drawButton(visible[i], x, y, btnW, COMPACT_BTN_H, units);
      }
    }
  }

  private drawButton(
    btn: CommandButton,
    x: number,
    y: number,
    w: number,
    h: number,
    units: Unit[],
  ): void {
    const enabled = btn.enabled(units);
    const colour  = enabled ? btn.colour : 0x2a3a38;
    const alpha   = enabled ? 1.0 : 0.42;
    const compact = h < BTN_H;

    const g = new Graphics();
    const drawBg = (hover: boolean) => {
      g.clear();
      g.beginFill(hover && enabled ? 0x152d27 : 0x0d1f1a, 0.94);
      g.lineStyle(1, hover && enabled ? colour : PANEL_BDR, hover && enabled ? 0.9 : 0.35);
      g.drawRoundedRect(0, 0, w, h, 3);
      g.endFill();
      g.lineStyle(0);
    };
    drawBg(false);
    g.position.set(x, y);
    g.alpha = alpha;

    if (enabled) {
      g.eventMode = 'static';
      g.cursor    = 'pointer';
      g.on('pointerover',  () => drawBg(true));
      g.on('pointerout',   () => drawBg(false));
      g.on('pointerdown',  () => { btn.execute(this.currentUnits); this.rebuild(); });
    } else {
      g.eventMode = 'none';
    }
    this.panel.addChild(g);

    // Hotkey key-cap — hide on compact buttons with no hotkey
    if (btn.hotkey) {
      const capW = compact ? 13 : 16;
      const capH = compact ? 11 : 13;
      const cap = new Graphics();
      cap.beginFill(0x0a1810, 0.85);
      cap.lineStyle(1, colour, enabled ? 0.4 : 0.15);
      cap.drawRoundedRect(0, 0, capW, capH, 2);
      cap.endFill();
      cap.position.set(x + 4, y + 4);
      cap.eventMode = 'none';
      this.panel.addChild(cap);

      const capTxt = new Text(btn.hotkey.toUpperCase(), new TextStyle({
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: compact ? 8 : 9,
        fill: `#${colour.toString(16).padStart(6, '0')}`,
      }));
      capTxt.anchor.set(0.5, 0.5);
      capTxt.position.set(x + 4 + capW / 2, y + 4 + capH / 2);
      capTxt.eventMode = 'none';
      this.panel.addChild(capTxt);
    }

    // Main label
    const lblY  = compact ? y + (btn.hotkey ? 14 : 8) : y + 26;
    const lbl = new Text(btn.label, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: compact ? 9 : 11,
      fontWeight: 'bold',
      fill: `#${colour.toString(16).padStart(6, '0')}`,
      letterSpacing: 0.5,
      wordWrap: true,
      wordWrapWidth: w - 4,
    }));
    lbl.anchor.set(0.5, 0);
    lbl.position.set(x + w / 2, lblY);
    lbl.eventMode = 'none';
    this.panel.addChild(lbl);

    // Sub-label (static string or dynamic function)
    const subText = typeof btn.sub === 'function' ? btn.sub(units) : btn.sub;
    if (subText) {
      const subY = compact ? lblY + 14 : y + 44;
      const sub = new Text(subText, new TextStyle({
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: compact ? 8 : 9,
        fill: '#2d5050',
        wordWrap: true,
        wordWrapWidth: w - 4,
      }));
      sub.anchor.set(0.5, 0);
      sub.position.set(x + w / 2, subY);
      sub.eventMode = 'none';
      this.panel.addChild(sub);
    }
  }
}
